import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { FinancialDocument } from 'src/features/financialDocuments/utils/types';
import { LIST_FINANCIAL_DOCUMENTS_RESOURCE } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { useIssueFinancialDocument } from 'src/features/financialDocuments/api/useIssueFinancialDocument';
import { FINANCIAL_DOCUMENT_RESOURCE } from 'src/features/financialDocuments/api/useGetFinancialDocument';
import { useOnError } from 'src/utils/errors';

interface IssueFinancialDocumentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedFinancialDocument: FinancialDocument;
    children?: React.ReactNode;
}

function IssueFinancialDocumentFormModal<TData>({
    selectedFinancialDocument,
    children,
}: IssueFinancialDocumentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const { mutate: issueFinancialDocument, isPending } = useIssueFinancialDocument({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentsMessages.issueFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.issueFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => {
            setIsModalOpen(false);
            // No form fields to attach a 400 to, so every error is shown as a toast.
            onError(error);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...financialDocumentsMessages.issueFinancialDocument },
                subTitle: {
                    ...financialDocumentsMessages.issueFinancialDocumentSubtitle,
                },
                icon: ICON_MAP.INVOICE,
                confirmationMessage: { ...financialDocumentsMessages.confirmIssueFinancialDocument },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: financialDocumentsMessages.issue,
                // The dialog stays open, with the button disabled, until the request settles: closing
                // it straight away left a window where a second click issued the document again.
                isPending,
                callbackFn: () => issueFinancialDocument({ documentId: selectedFinancialDocument.id }),
            }}
            children={children}
        />
    );
}

export default IssueFinancialDocumentFormModal;
